import { ElementRef, NgZone, inject } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { LoadCurrentNewsfeedFiltersAction } from './states/newsfeed-state/newsfeed.actions';
import { LoadCurrentTenantAction } from './states/tenant-state/tenant.actions';

import { AuthenticationService } from '../shared/services/authentication.service';
import { HealthCheckService } from '../shared/services/health-check.service';
import { ThemingService } from '../shared/services/theming.service';
import { PushNotificationService } from '../shared/services/push-notification.service';
import { TextZoom } from '@capacitor/text-zoom';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import {
  handleResize,
  setPageTransitionAnimated
} from './states/layout-state/layout.actions';
import { NavigationEnd, Router } from '@angular/router';
import { CookiebotService, LoadingService } from '@bling-fe/shared/services';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  fromEvent,
  map,
  startWith,
  tap
} from 'rxjs';

export abstract class AppComponentBase {
  protected store = inject(Store);
  private element = inject<ElementRef<HTMLElement>>(ElementRef);
  private platform = inject(Platform);
  private checkHealthService = inject(HealthCheckService);
  private themingService = inject(ThemingService);
  private authenticationService = inject(AuthenticationService);
  private pushNotificationService = inject(PushNotificationService);
  private router = inject(Router);
  private zone = inject(NgZone);
  private loadingService = inject(LoadingService);
  private cookiebotService = inject(CookiebotService);

  constructor() {
    CapacitorUpdater.notifyAppReady();
    this.initializeApp();
    this.showLoadingUntilNavigationFinished();
  }

  initializeApp() {
    this.platform.ready().then(async () => {
      this.dispatchStartupActions();
      this.checkHealthService.checkVersion();
      this.themingService.init();
      this.authenticationService.init();
      this.pushNotificationService.init();
      this.setPreferedZoom();

      this.cookiebotService.initializeCookiebot();

      this.setPageTransitions();
      this.watchWindowResize();
    });
  }

  protected dispatchStartupActions(): void {
    this.store.dispatch(new LoadCurrentTenantAction());
    this.store.dispatch(new LoadCurrentNewsfeedFiltersAction());
  }

  private showLoadingUntilNavigationFinished() {
    const firstNavigationEnd = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      first()
    );

    let isLoading = false;

    firstNavigationEnd
      .pipe(
        map(() => 'dismissLoading'),
        startWith('showLoading'),
        debounceTime(200),
        tap(action => {
          if (action === 'showLoading' && !isLoading) {
            this.loadingService.show();
            isLoading = true;
          } else if (action === 'dismissLoading' && isLoading) {
            this.loadingService.dismiss();
            isLoading = false;
          }
        })
      )
      .subscribe();
  }

  // sets base font size for ios, where it doesn't work out-of-the-box
  private async setPreferedZoom() {
    if (Capacitor.isNativePlatform()) {
      const zoom = await TextZoom.getPreferred();
      document.querySelector('html').style.fontSize = `${zoom.value * 100}%`;
    }
  }

  private setPageTransitions() {
    if (Capacitor.isNativePlatform()) {
      this.store.dispatch(setPageTransitionAnimated({ animated: true }));
    } else {
      this.store.dispatch(setPageTransitionAnimated({ animated: false }));
    }
  }

  /**
   * Observe window.resize event and update layout store accordingly.
   */
  private watchWindowResize() {
    this.zone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(startWith(null), debounceTime(300), distinctUntilChanged())
        .subscribe(() => {
          this.zone.run(() => {
            this.store.dispatch(
              handleResize({ width: this.element.nativeElement.offsetWidth })
            );
          });
        });
    });
  }
}
